import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  keyframes,
} from "@chakra-ui/react";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { XCircle, Sparkles, Download, ArrowLeft, Heart } from "lucide-react";

interface SeeYouNextProps {
  onBack: () => void;
  proposal: any;
}

const scaleIn = keyframes`
  0% { transform: scale(0) rotate(-180deg); opacity: 0; }
  50% { transform: scale(1.1) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const wave = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-20deg); }
`;

const SeeYouNext: React.FC<SeeYouNextProps> = ({ onBack, proposal }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowContent(true), 300);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, orange.50, red.50, pink.50)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      p={4}
    >
      {/* Background Decorative Elements */}
      <Box
        position="absolute"
        top="10%"
        left="10%"
        w="300px"
        h="300px"
        borderRadius="full"
        bgGradient="linear(to-br, orange.200, red.200)"
        opacity={0.2}
        filter="blur(80px)"
        animation={`${float} 6s ease-in-out infinite`}
      />
      <Box
        position="absolute"
        bottom="10%"
        right="10%"
        w="250px"
        h="250px"
        borderRadius="full"
        bgGradient="linear(to-br, red.200, pink.200)"
        opacity={0.2}
        filter="blur(80px)"
        animation={`${float} 8s ease-in-out infinite`}
      />

      {/* Main Content */}
      <VStack
        spacing={8}
        bg="white"
        borderRadius="3xl"
        boxShadow="2xl"
        p={{ base: 8, md: 12 }}
        maxW="600px"
        w="full"
        position="relative"
        border="2px solid"
        borderColor="orange.100"
        animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
      >
        {/* Icon with Wave Animation */}
        <Box position="relative">
          <Box animation={`${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)`}>
            <Box
              bgGradient="linear(to-br, orange.400, red.500)"
              borderRadius="full"
              p={8}
              boxShadow="0 20px 40px -10px rgba(249, 115, 22, 0.4)"
              position="relative"
            >
              <Text
                fontSize="6xl"
                animation={`${wave} 2s ease-in-out infinite`}
                display="inline-block"
              >
                👋
              </Text>

              {/* Pulsing Ring */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="full"
                h="full"
                borderRadius="full"
                border="4px solid"
                borderColor="orange.300"
                animation={`${float} 2s ease-in-out infinite`}
                opacity={0.6}
              />
            </Box>
          </Box>
        </Box>

        {/* Text */}
        <VStack spacing={3}>
          <Text
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="900"
            bgGradient="linear(to-r, orange.600, red.600)"
            bgClip="text"
            textAlign="center"
            animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
          >
            See You Next Time!
          </Text>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="700"
            color="gray.800"
            textAlign="center"
            animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
          >
            Thank you for your feedback
          </Text>
        </VStack>

        {/* Message Box */}
        <Box
          bg="orange.50"
          border="2px solid"
          borderColor="orange.200"
          borderRadius="2xl"
          p={6}
          w="full"
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          <VStack spacing={3} align="start">
            <HStack spacing={2}>
              <Icon as={Heart} w={5} h={5} color="orange.600" />
              <Text fontSize="sm" fontWeight="700" color="orange.900">
                We appreciate your time
              </Text>
            </HStack>
            <VStack align="start" spacing={2} pl={7}>
              <Text fontSize="sm" color="orange.800" lineHeight="1.6">
                • Your feedback has been recorded successfully
              </Text>
              <Text fontSize="sm" color="orange.800" lineHeight="1.6">
                • The proposal owner will be notified
              </Text>
              <Text fontSize="sm" color="orange.800" lineHeight="1.6">
                • We'd love to work with you in the future
              </Text>
              <Text fontSize="sm" color="orange.800" lineHeight="1.6">
                • Feel free to reach out if anything changes
              </Text>
            </VStack>
          </VStack>
        </Box>

        {/* Quote Box */}
        <Box
          bg="gradient.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          p={4}
          w="full"
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
          textAlign="center"
        >
          <Text
            fontSize="sm"
            color="gray.700"
            fontStyle="italic"
            lineHeight="1.6"
          >
            "Every no brings us closer to a yes. Thank you for considering our
            proposal."
          </Text>
          <Text fontSize="xs" color="gray.500" mt={2} fontWeight="600">
            — {proposal?.companyInfo?.companyName}
          </Text>
        </Box>

        {/* Action Buttons */}
        <VStack
          w="full"
          spacing={3}
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          <Button
            leftIcon={<ArrowLeft size={18} />}
            size="lg"
            w="full"
            fontSize="sm"
            fontWeight="700"
            variant="outline"
            borderWidth="2px"
            borderColor="gray.300"
            color="gray.700"
            _hover={{
              borderColor: "gray.400",
              bg: "gray.50",
              transform: "translateY(-2px)",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            transition="all 0.2s"
            onClick={handleRefresh}
          >
            View Proposal Again
          </Button>
        </VStack>

        {/* Footer Note */}
        <Text
          fontSize="xs"
          color="gray.500"
          textAlign="center"
          animation={showContent ? `${slideUp} 0.6s ease-out` : "none"}
        >
          Questions? Reach us at {proposal?.companyInfo?.Email}
        </Text>
      </VStack>
    </Box>
  );
};

export default SeeYouNext;
